"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cx } from "../internal/cx.js";
import { IconX } from "../internal/icons.js";
export const TOAST_MS = 5000;
export const TOASTS_VISIBLE = 3;
/** One toast line: text, its action, «×»; the timer pauses under the pointer and in focus (П337). */
function ToastView({ text, action, ms = TOAST_MS, tone = "info", onClose, className }) {
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const [held, setHeld] = useState(false);
    const sticky = tone === "error" || !ms;
    useEffect(() => {
        if (sticky || held)
            return;
        const timer = window.setTimeout(() => closeRef.current(), ms);
        return () => window.clearTimeout(timer);
    }, [ms, sticky, held]);
    return _jsxs("div", { className: cx("jui-toast", `jui-toast--${tone}`, className), role: tone === "error" ? "alert" : "status", onPointerEnter: () => setHeld(true), onPointerLeave: () => setHeld(false), onFocus: () => setHeld(true), onBlur: () => setHeld(false), children: [_jsx("span", { className: "jui-toast-text", children: text }), action && _jsx("button", { type: "button", className: "jui-toast-action", onClick: () => { action.run(); closeRef.current(); }, children: action.label }), _jsx("button", { type: "button", className: "jui-toast-close", "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", onClick: () => closeRef.current(), children: _jsx(IconX, { small: true }) })] });
}
const stackOf = (children, container) => {
    const body = _jsx("div", { className: "jui-toasts", children: children });
    return typeof document === "undefined" ? body : createPortal(body, container ?? document.body);
};
/** A single toast (kept for screens that manage one themselves). */
export function Toast({ container, ...props }) {
    return stackOf(_jsx(ToastView, { ...props }), container);
}
/** The queue: at most 3 on screen, the next one takes a freed place (П335). */
function useQueue() {
    const [items, setItems] = useState([]);
    const counter = useRef(0);
    const push = useCallback((next) => { const id = ++counter.current; setItems(list => [...list, { ...next, id }]); return id; }, []);
    const drop = useCallback((id) => setItems(list => list.filter(item => item.id !== id)), []);
    const clear = useCallback(() => setItems([]), []);
    return { shown: items.slice(0, TOASTS_VISIBLE), push, drop, clear };
}
/**
 * Toasts of one screen: `show({ text, action, tone })` queues one; render `toast` anywhere once.
 * `const { show, toast } = useToast();`
 */
export function useToast(container) {
    const queue = useQueue();
    const center = useContext(CenterContext);
    const show = useCallback((next) => { center?.record(next); return queue.push(next); }, [center, queue.push]);
    const toast = queue.shown.length > 0
        ? stackOf(queue.shown.map(item => _jsx(ToastView, { text: item.text, action: item.action, ms: item.ms, tone: item.tone, onClose: () => queue.drop(item.id) }, item.id)), container)
        : null;
    return { show, hide: queue.clear, toast };
}
const CenterContext = createContext(null);
/** The notification centre of a product: every toast shown under it is kept here (П338). AppShell puts it in by itself. */
export function NotificationsProvider({ children }) {
    const [notices, setNotices] = useState([]);
    const counter = useRef(0);
    const record = useCallback((next) => {
        setNotices(list => [{ id: ++counter.current, text: next.text, tone: next.tone ?? "info", at: new Date(), read: false }, ...list].slice(0, 50));
    }, []);
    const readAll = useCallback(() => setNotices(list => (list.some(notice => !notice.read) ? list.map(notice => ({ ...notice, read: true })) : list)), []);
    const clear = useCallback(() => setNotices([]), []);
    const value = useMemo(() => ({ notices, unread: notices.filter(notice => !notice.read).length, record, readAll, clear }), [notices, record, readAll, clear]);
    return _jsx(CenterContext.Provider, { value: value, children: children });
}
/** The centre of the nearest NotificationsProvider (null outside one). */
export const useNotifications = () => useContext(CenterContext);
